// The only import for the app (docs/AUDIO_DESIGN.md §0):
//   const player = createPlayer(); await player.unlock(); player.play() / pause() / toggle() / next(id?)
//   player.setMusicVolume(v) · setAmbienceVolume(v) · setSoundTrim(id, v) · setScene(scene, time) · setVibe({ energy, band, style })
//   player.setWeather(weather)   (the Weather chip: the city sounds and the track titles follow it)
//   player.setRadio(on) · player.radio · player.setPicks(list) · player.vote   (Lofi Cities Radio, site/js/audio/radio.js)
//   player.on('state' | 'track' | 'queue', cb) → off · player.cue(type) · player.fadeOut(sec)   (focus / sleep timers)
//   player.tap(sound, { x })   (a tapped spot in the scene, site/js/ui/spots.js: on the next beat, in the track's key)
//   player.jam(i)   (Jam, site/js/audio/jam.js: key i of the eight that fit the chord, on the next 8th note)
//   player.setLayer(id, on) · player.layers · player.on('layers', cb)   (scene layers, site/js/audio/layers.js: a part
//                                                                         that joins or stops on the next bar line)
//   player.state · player.track · player.queue · player.sounds · player.vibe · player.debug() · player.dispose()   (tests)
import { unlock as unlockContext, close as closeContext, whenRunning, keepAlive, watch, latencyOf, running } from './context.js';
import { createClock } from './clock.js';
import { createRegistry, hold } from './dsp.js';
import { createMixer } from './mixer.js';
import { createEngine } from './engine.js';
import { normVibe } from './arrange.js';
import { createAmbience, soundsOf, weatherScene } from './ambience.js';
import { createPlaylist } from './playlist.js';
import { EVENTS } from './sounds.js';
import { TAP_MAX, TAP_VOICES, nextBeat, tapNotes, playTap } from './taps.js';
import { JAM_KEYS, JAM_AHEAD, nextEighth, eighthLate, jamScale, jamNotes, jamVoice, jamLow, createJam } from './jam.js';
import { LAYER_IDS, createLayers } from './layers.js';
import { tuneSlot, slotAfter, slotOf, upcoming, radioShow, radioHour, radioStyle, voteOptions, voteAfter } from './radio.js';

const HORIZON = { visible: 0.3, hidden: 4 };          // seconds scheduled ahead (ticks: 25 ms visible, 250 ms hidden)
// Timer cues (player.cue): `gain` on the sounds.js one-shot (clamped to 0..2) balances the types, and CUE_TRIM lifts them
// all, since the one-shots are mixed as distant scene sounds. Calibrated with window.__runTimerCheck (tools/browser/audio-check.html):
// at the default sliders the loudest 400 ms of a cue sits ~6 dB over the mix, peaking under −3 dBFS.
export const CUE_GAIN = { chime: 2, bell: 1.5 }, CUE_TRIM = 2.3;
// On the radio, a track more than RESYNC_S from where the station has it is tuned in again (a pause, a frozen tab, the
// clock corrected); one more than NUDGE_S off by the context's output timestamp is moved there (just after the context
// starts or resumes it has no timestamp yet and reports no latency, which leaves a first tune-in 20-70 ms off). Both
// well above the clocks' jitter (a few ms), so neither fires on noise.
const RESYNC_S = 0.25, NUDGE_S = 0.02, RADIO_CHECK_MS = 250;
const clamp01 = v => Math.min(1, Math.max(0, +v || 0));
const coarse = () => { try { return !!(globalThis.matchMedia && matchMedia('(pointer: coarse)').matches); } catch { return false; } };
const randomSeed = () => {
  try { return crypto.getRandomValues(new Uint32Array(1))[0]; } catch { return Math.floor(Math.random() * 4294967296) >>> 0; }
};

/** `wallClock()` is epoch ms on the radio's clock: the live worker's (site/js/live/live.js serverNow) where the page has
 *  one, so every listener agrees on it within the network's one-way delay. */
export function createPlayer({
  lite = coarse(), seed = randomSeed(), siteRoot = new URL('../../', import.meta.url), wallClock = () => Date.now(),
} = {}) {
  const registry = createRegistry();
  const listeners = { state: new Set(), track: new Set(), queue: new Set(), layers: new Set() };
  let ac = null, mixer = null, engine = null, amb = null, playlist = null, clock = null;
  let state = 'locked', track = null, scene = null, time = () => performance.now() / 1000, vibe = normVibe(null), weather = 'city';
  const heard = () => weatherScene(scene, weather);   // the scene as it sounds in the picked weather
  let musicVol = 0.8, ambVol = 0.6, want = false, starting = null, suspendTimer = 0, suspended = false, fadeTimer = 0, cueBus = null;
  let unwatch = null, disposed = false;
  const trims = {};                                   // sound id → 0..2 (1 = as the city was designed)
  let hidden = typeof document !== 'undefined' && document.hidden, horizon = hidden ? HORIZON.hidden : HORIZON.visible;
  const tickStats = { n: 0, totalMs: 0, maxMs: 0, lastMs: 0, slow: 0 };
  let radio = false, radioAt = 0, resyncs = 0, nudges = 0, planning = 0;
  const picks = new Map(), NO_PICKS = new Map();        // the votes' winners: hour → slot index → style
  const picksOf = h => picks.get(h) ?? NO_PICKS;
  const reported = new Set();
  let tapEnds = [], tapped = 0, lastTap = null;         // taps still sounding (the ac times they end by), and for tests
  let jamKit = null, jamLog = [];                       // the Jam voices (built on the first note), and its last notes for tests
  let layerKit = null;                                  // the scene layers (built when the first one is switched on)
  const layerAsked = new Set();                         // the ones switched on before there was a track to join

  // the playlist config loads in the background (cheap, no AudioContext); a failure means generative mode
  // (`src` is relative to the site root, so it resolves the same on the home page and on the city pages)
  let entries = null;
  const entriesReady = import('../../music/playlist.js')
    .then(m => {
      entries = Array.isArray(m.default)
        ? m.default.filter(e => e && typeof e.src === 'string').map(e => ({ ...e, src: new URL(e.src, siteRoot).href }))
        : [];
    })
    .catch(() => { entries = []; });

  const emit = (ev, v) => { for (const cb of [...listeners[ev]]) { try { cb(v); } catch (e) { console.error(e); } } };
  const setState = s => { if (s !== state && !disposed) { state = s; emit('state', s); } };
  const report = e => { const k = String(e && e.message); if (!reported.has(k)) { reported.add(k); console.error('[audio]', e); } };
  // a radio track also says when it started (`at`, epoch ms on the radio's clock) and its hour's show style (a vote's
  // winner has another); its city is its title's host. A Classical track names the piece it covers ({ id, name, by }).
  const trackInfo = p => ({
    title: p.title, artist: p.radio ? 'Lofi Cities Radio' : `Lofi Cities · ${p.city}`, city: p.city, source: 'generative', seed: p.seed,
    bpm: p.bpm, key: p.keyName, duration: p.duration, energy: p.vibe.energy, band: p.vibe.band, style: p.vibe.style, mix: !!p.mix,
    ...(p.piece && { piece: { ...p.piece } }),
    ...(p.radio && { radio: true, at: p.radio.start, show: radioStyle(p.radio.h) }),
  });
  const setTrack = info => { track = info; emit('track', info); emit('queue', player.queue); };
  // cues bypass both faders but follow the louder slider (v² like the faders), so turning everything down or muting silences them
  const cueLevel = () => CUE_TRIM * Math.max(musicVol, ambVol) ** 2;
  const setCueLevel = () => { if (cueBus) { const t = ac.currentTime; hold(cueBus.gain, t); cueBus.gain.setTargetAtTime(cueLevel(), t, 0.05); } };
  const cues = () => { if (!cueBus) { cueBus = ac.createGain(); cueBus.gain.value = cueLevel(); cueBus.connect(mixer.master); } return cueBus; };

  /* ---- Lofi Cities Radio (site/js/audio/radio.js, docs/AUDIO_DESIGN.md §13) ---- */
  // A station slot as the engine plays it: the listener's band on top, tagged with where it is on the station.
  const radioPlan = s => ({ ...s.plan, vibe: { ...s.plan.vibe, band: vibe.band }, radio: { h: s.h, i: s.i, start: s.start } });
  // Whether two plans are the same station track: a vote's winner keeps its slot's seed (and start) but not its style,
  // and so not its length either; a pick before it can change its key.
  const sameTrack = (a, b) => a.seed === b.seed && a.vibe.style === b.vibe.style && a.bars === b.bars && a.bpm === b.bpm && a.keyName === b.keyName;
  // The ac time heard at epoch ms `ms` on the radio's clock: from the context's output timestamp where it has one (the
  // output latency is in it), else from currentTime less the latency the context reports. `exactOnly`: null unless from
  // the timestamp. A timestamp far from the rough time is stale (just after a resume) or the browser's bug.
  function acOf(ms, exactOnly = false) {
    const now = wallClock(), rough = ac.currentTime + (ms - now) / 1000 - latencyOf(ac);
    const ts = typeof ac.getOutputTimestamp === 'function' ? ac.getOutputTimestamp() : null;
    const exact = ts && ts.contextTime > 0 && ts.performanceTime > 0
      ? ts.contextTime + (ms - now + performance.now() - ts.performanceTime) / 1000 : null;
    if (exact !== null && Math.abs(exact - rough) < 0.1) return exact;
    return exactOnly ? null : rough;
  }
  // engine.follow: after a station track, the station's next one, when the station starts it
  function followRadio(plan) {
    const was = plan.radio && slotOf(plan.radio, picksOf), s = was ? slotAfter(was, picksOf) : tuneSlot(wallClock(), picksOf);
    return { plan: radioPlan(s), start: acOf(s.start) };
  }
  function tuneRadio() {
    const s = tuneSlot(wallClock(), picksOf);
    engine.tune(radioPlan(s), acOf(s.start));
    resyncs++;
  }
  // The track playing must be the station's, from when the station started it: tune in again when it isn't (radio just
  // switched on, back from a pause: the context's clock stood still meanwhile; a vote's winner that came too late for
  // the track already built) or has drifted over RESYNC_S (a frozen tab, the clock corrected). During an hour's last
  // track, the next hour is planned ahead, off the scheduler's tick.
  function keepRadio() {
    if (!radio || !engine || !ac || !running(ac)) return;
    const p = engine.plan, s = p && p.radio ? slotOf(p.radio, picksOf) : null;
    if (!s || !sameTrack(s.plan, p) || Math.abs(engine.startTime - acOf(s.start)) > RESYNC_S) { tuneRadio(); return; }
    const exact = acOf(s.start, true);
    if (exact !== null && Math.abs(engine.startTime - exact) > NUDGE_S) { engine.nudge(exact - engine.startTime); nudges++; }
    const last = s.i === radioHour(s.h, picksOf(s.h)).slots.length - 1;
    if (!planning && last) planning = setTimeout(() => radioHour(s.h + 1, picksOf(s.h + 1)), 0);
    else if (planning && !last) planning = 0;
  }
  // a tune-in again of the track already showing is not a new track (a late vote's winner in its place is)
  const onTrack = p => {
    if (p.radio && track && track.radio && track.at === p.radio.start && track.seed === p.seed && track.style === p.vibe.style
      && track.key === p.keyName && track.duration === p.duration) return;
    setTrack(trackInfo(p));
  };

  function tick() {
    if (!ac || !running(ac)) return;
    const t0 = performance.now(), until = ac.currentTime + horizon;
    try {
      if (radio && t0 - radioAt >= RADIO_CHECK_MS) { radioAt = t0; keepRadio(); }
      if (engine) engine.advance(until);
      if (engine && (layerKit || layerAsked.size)) advanceLayers(until);
      if (amb) amb.advance(until);
      if (playlist) playlist.advance(ac.currentTime);
    } catch (e) { report(e); }
    const d = performance.now() - t0;
    tickStats.n++; tickStats.totalMs += d; tickStats.lastMs = d; if (d > tickStats.maxMs) tickStats.maxMs = d; if (d > 0.5) tickStats.slow++;
  }

  /* ---- scene layers (site/js/audio/layers.js) ---- */
  // The parts are scheduled after the engine's own notes, from the bars it has just planned. Not one of their nodes
  // exists until a layer is switched on, so a visit without one plays through the graph it always did.
  function advanceLayers(until) {
    if (!layerKit) {
      if (!engine.plan) return;
      layerKit = createLayers(ac, mixer, { registry, lite });
      for (const id of layerAsked) layerKit.set(id, true, engine);
      layerAsked.clear();
    }
    if (layerKit.advance(until, engine, ac.currentTime, latencyOf(ac))) emit('layers', player.layers);
  }
  function clearLayers() {
    if (!layerAsked.size && !(layerKit && Object.values(layerKit.states(engine)).some(l => l.want || l.on))) return;
    layerAsked.clear();
    if (layerKit) layerKit.clear();
    emit('layers', player.layers);
  }

  function startEngine() {
    if (engine) return;
    mixer.vinyl.gain.setTargetAtTime(1, ac.currentTime, 0.3);
    engine = createEngine(ac, mixer, { scene: heard(), vibe, lite, registry, onTrack });
    if (radio) { engine.follow(followRadio); tuneRadio(); }
    else engine.start(seed, ac.currentTime + 0.15);
  }
  function startPlaylist() {
    try {
      playlist = createPlaylist(ac, mixer, entries, {
        onTrack: setTrack,
        onFail: () => { const p = playlist; playlist = null; if (p) p.dispose(); startEngine(); },
      });
      mixer.vinyl.gain.setTargetAtTime(0, ac.currentTime, 0.3);     // no vinyl in playlist mode
      playlist.play();
    } catch (e) { playlist = null; startEngine(); }
  }
  function onVisibility() {
    hidden = document.hidden;
    horizon = hidden ? HORIZON.hidden : HORIZON.visible;
    if (clock) clock.setHidden(hidden);
    if (hidden || !ac) return;
    if (want && !running(ac)) {
      ac.resume().catch(() => {});
      setTimeout(() => { if (want && !running(ac)) setState('interrupted'); }, 300);
    }
    tick();
  }
  function onPageShow(e) { if (e.persisted && state !== 'locked') player.pause(); }
  // Closing the tab (or leaving the page) has to silence it right away: Safari keeps a tab's audio process, the iOS
  // keep-alive <audio> and the lock-screen Now Playing alive for a while after the tab is gone, and a page frozen in the
  // back/forward cache can keep sounding. So stop hard, with no fade (there are no more frames to run one):
  // leaving for good closes the context, a page that may come back from the cache is paused (onPageShow keeps it so).
  function onPageHide(e) {
    if (!ac || disposed) return;
    if (!e || !e.persisted) { player.dispose(); return; }
    want = false;
    clearTimeout(fadeTimer); fadeTimer = 0;
    if (mixer) { const g = mixer.master.gain, now = ac.currentTime; hold(g, now); g.setValueAtTime(0, now); }
    setState('paused');
    suspendNow();
  }

  async function boot() {
    const ok = await whenRunning(ac, 2000);
    await Promise.race([entriesReady, new Promise(r => setTimeout(r, 1500))]);
    if (disposed) return;
    if (!mixer) mixer = createMixer(ac, { lite, musicVolume: musicVol, ambienceVolume: ambVol, registry });
    amb = createAmbience(ac, mixer, { key: () => (engine ? engine.key() : null), registry, seed, trims });
    if (scene) amb.setScene(heard(), time, 3);
    if (!playlist && !engine) { if (entries && entries.length) startPlaylist(); else startEngine(); }
    clock = createClock(tick);
    clock.setHidden(hidden);
    clock.start();
    tick();
    starting = null;
    if (!want) { suspendNow(); setState('paused'); return; }
    setState(ok && running(ac) ? 'playing' : 'interrupted');
  }
  function suspendNow() {
    clearTimeout(suspendTimer);
    if (playlist) playlist.pause();
    keepAlive(false);
    suspended = true;
    ac.suspend().catch(() => {});
  }

  // for tests: how far (s) the engine's track is from where the station has it, and how often it was tuned in
  function radioDebug() {
    if (!radio) return null;
    const p = engine && engine.plan, s = p && p.radio ? slotOf(p.radio, picksOf) : null;
    return { resyncs, nudges, slot: s ? { h: s.h, i: s.i, seed: s.plan.seed } : null,
      drift: s && ac && running(ac) ? +(engine.startTime - acOf(s.start)).toFixed(4) : null };
  }

  const player = {
    /** Call synchronously inside a click / keydown / touchend handler (or at load where autoplay is allowed, e.g. OBS). */
    unlock() {
      if (disposed) return Promise.resolve();
      if (starting) {                                    // a gesture during start-up (after a blocked autoplay attempt) resumes the context
        want = true;
        if (!running(ac)) unlockContext();
        return starting;
      }
      if (ac) {                                          // already unlocked: a gesture while paused or interrupted
        if (state !== 'playing') player.play();
        return Promise.resolve();
      }
      want = true;
      setState('starting');
      try { ac = unlockContext(); }
      catch (e) { setState('locked'); return Promise.reject(e); }
      unwatch = watch(ac, {
        onInterrupted: () => { if (want && !starting && !suspended) setState('interrupted'); },
        onRunning: () => { if (want && !starting && state !== 'playing') { setState('playing'); if (amb) amb.resync(); keepRadio(); } },
      });
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
      if (typeof addEventListener === 'function') { addEventListener('pageshow', onPageShow); addEventListener('pagehide', onPageHide); }
      if (entries && entries.length) {                   // playlist known already: start the decks inside the gesture (iOS)
        try { mixer = createMixer(ac, { lite, musicVolume: musicVol, ambienceVolume: ambVol, registry }); startPlaylist(); }
        catch (e) { report(e); }
      }
      starting = boot().catch(e => { starting = null; report(e); setState('interrupted'); });
      return starting;
    },
    play() {
      if (!ac) return;                                   // locked: needs unlock() inside a gesture
      want = true;
      clearTimeout(suspendTimer);
      clearTimeout(fadeTimer); fadeTimer = 0;            // also cancels a fadeOut in progress (the gain glides back to 1 below)
      if (starting) return;
      const wasSuspended = suspended || !running(ac);
      suspended = false;
      keepAlive(true);
      setState('playing');
      ac.resume().then(() => {
        if (!want) return;
        keepRadio();                                     // the station went on without us: tune in before the fade-in
        const now = ac.currentTime, g = mixer.master.gain;
        hold(g, now); g.setTargetAtTime(1, now, 0.08);
        if (wasSuspended && amb) amb.resync();
        if (playlist) playlist.play();
        tick();
      }, () => setState('interrupted'));
      setTimeout(() => { if (want && ac && !running(ac)) setState('interrupted'); }, 1000);
    },
    pause() {
      if (starting) { want = false; return; }
      if (!ac || state === 'paused' || state === 'locked') return;
      want = false;
      setState('paused');
      clearTimeout(fadeTimer); fadeTimer = 0;
      const now = ac.currentTime, g = mixer.master.gain;
      hold(g, now); g.setTargetAtTime(0, now, 0.06);    // 0.25 s fade, then freeze the timeline
      clearTimeout(suspendTimer);
      suspendTimer = setTimeout(() => { if (!want) suspendNow(); }, 260);
    },
    /** sleep timer: fade music and ambience out over `sec` seconds (even in dB, -40 dB at the end), then pause.
     *  play() or pause() during the fade cancels it; calling fadeOut again restarts it from the current level. */
    fadeOut(sec) {
      if (!ac || state !== 'playing') return;
      const d = Math.max(0.1, +sec || 0), now = ac.currentTime, g = mixer.master.gain;
      hold(g, now); g.setTargetAtTime(0, now, d / 4.6);
      clearTimeout(fadeTimer);
      fadeTimer = setTimeout(() => { fadeTimer = 0; if (state === 'playing') player.pause(); }, d * 1000);
    },
    /** timer sound: an EVENTS one-shot ('chime' | 'bell') in the current track's key, at the level of the louder slider.
     *  Only while playing (a paused context is suspended); returns whether it was scheduled. */
    cue(type) {
      if (!ac || !mixer || starting || state !== 'playing' || !running(ac) || !EVENTS[type]) return false;
      const env = { registry, tag: 'cue', key: () => (engine ? engine.key() : null), echo: mixer.echoIn, r: Math.random };
      try { EVENTS[type](ac, cues(), ac.currentTime + 0.05, { type, pan: 0, gain: CUE_GAIN[type] ?? 1 }, env); }
      catch (e) { report(e); return false; }
      return true;
    },
    /** A tapped spot's sound (site/js/ui/spots.js KINDS): a taps.js one-shot on the track's next beat, so within a
     *  beat, in the key sounding then, panned to scene column `x`; at once in playlist mode, which has neither. On the
     *  cues' bus, so it follows the louder slider and mute. Only while playing, and never more than TAP_MAX at once.
     *  Returns { in: seconds until it is heard }, or null when nothing was scheduled. */
    tap(sound, { x = null } = {}) {
      if (!ac || !mixer || starting || state !== 'playing' || !running(ac) || !sound || !TAP_VOICES.includes(sound.voice)) return null;
      const now = ac.currentTime, plan = engine ? engine.plan : null;
      tapEnds = tapEnds.filter(end => end > now);
      if (tapEnds.length >= TAP_MAX) return null;
      const when = plan ? nextBeat(engine.startTime, plan.bpm, now) : now;
      const key = plan ? engine.key(when) : null, midis = tapNotes(sound, key, plan);
      const env = { registry, tag: 'tap', echo: mixer.echoIn, r: Math.random };
      try { tapEnds.push(playTap(ac, cues(), when, sound, midis, env, x)); }
      catch (e) { report(e); return null; }
      tapped++;
      lastTap = { at: +now.toFixed(4), when: +when.toFixed(4), beat: plan ? +(60 / plan.bpm).toFixed(4) : null, key, midis };
      return { in: when - now + latencyOf(ac) };
    },
    /** A Jam note (site/js/audio/jam.js): key `i` (0–7) of the eight notes that fit the chord sounding on the track's
     *  next 8th note, played on that 8th in a soft voice of the track's style, through the music bus (the record's tone
     *  and tape, the music slider and mute reach it), over the full band, no drums and chords only alike. Only while
     *  generated music plays: nothing before the first track, while paused, or in playlist mode, which has no chords
     *  to fit. Returns { in: seconds until it is heard, midi }, or null when nothing was scheduled (that note is
     *  already landing on that 8th). */
    jam(i) {
      if (!ac || !mixer || starting || state !== 'playing' || !running(ac) || !engine || !engine.plan || !Number.isInteger(i) || i < 0 || i >= JAM_KEYS) return null;
      const now = ac.currentTime, plan = engine.plan, style = plan.vibe.style, late = eighthLate(plan);
      const when = nextEighth(engine.startTime, plan.bpm, now + JAM_AHEAD, late), h = engine.harmony(when);
      const midi = jamNotes(h.tones, h.root, h.key, h.plan, jamLow(style))[i];
      try {
        jamKit ??= createJam(ac, mixer, { registry, lite });
        if (!jamKit.note(when, midi, style, i)) return null;
      } catch (e) { report(e); return null; }
      jamLog.push({ i, midi, at: +now.toFixed(4), when: +when.toFixed(4), start: +engine.startTime.toFixed(4), eighth: +(30 / plan.bpm).toFixed(4),
        late: +late.toFixed(4), bar: h.bar, step: h.step, key: h.key, root: h.root, chord: { root: h.chord.root, q: h.chord.q }, tones: h.tones, scale: jamScale(h.key, h.plan),
        style, voice: jamVoice(style) });
      if (jamLog.length > 64) jamLog.shift();
      return { in: when - now + latencyOf(ac), midi };
    },
    /** A scene layer (site/js/audio/layers.js LAYER_IDS: 'shaker', 'pad', 'twinkle') on or off: a part that plays along
     *  with the track, made from its tempo, key and chords, through the music bus (the music slider and mute reach
     *  it). It joins on the track's next bar line and stops on the next one, so within a bar; switched on before the
     *  first track or while paused, it joins on the first bar line the music reaches. Callable before unlock. Not on
     *  the radio, which plays the same song for everyone (tuning in switches every layer off), nor in playlist mode,
     *  which has no bars to join: there it returns null. Else { in: seconds until it changes, null while nothing
     *  plays }; player.layers has every layer's state, and 'layers' fires when one changes. */
    setLayer(id, on) {
      if (disposed || !LAYER_IDS.includes(id) || radio || playlist || (entries && entries.length && !engine)) return null;
      if (!layerKit && !(engine && engine.plan && ac && mixer)) {     // no track yet: it joins the first one
        if (on) layerAsked.add(id); else layerAsked.delete(id);
      } else {
        try {
          layerKit ??= createLayers(ac, mixer, { registry, lite });
          layerKit.set(id, on, engine);
        } catch (e) { report(e); return null; }
      }
      const l = player.layers[id];
      emit('layers', player.layers);
      if (ac && running(ac) && state === 'playing') tick();
      return { in: l.in };
    },
    /** { id: { want: switched on, on: sounding, fits: the track playing has a place for it (the shaker rests in a
     *  track without a beat), in: seconds until `on` becomes `want`, null when they agree or nothing plays } } */
    get layers() {
      const st = layerKit ? layerKit.states(engine) : null, live = ac && running(ac) && state === 'playing';
      return Object.fromEntries(LAYER_IDS.map(id => {
        const l = st ? st[id] : { want: layerAsked.has(id), on: false, fits: true, at: null };
        return [id, { want: l.want, on: l.on, fits: l.fits, in: live && l.at !== null ? Math.max(0, l.at - ac.currentTime) + latencyOf(ac) : null }];
      }));
    },
    toggle() {
      if (state === 'playing' || state === 'starting') player.pause();
      else if (state === 'locked') return player.unlock();
      else player.play();
    },
    /** skip to the next track, or to the player.queue item with this id; the queue event follows when it starts */
    next(id) {
      if (!ac || starting || (radio && engine)) return;  // the radio plays the same song for everyone: no skipping
      try { if (playlist) playlist.next(id); else if (engine) engine.next(id); } catch (e) { report(e); }
      tick();
    },
    setMusicVolume(v) { musicVol = clamp01(v); if (mixer) mixer.setMusicVolume(musicVol); setCueLevel(); },
    setAmbienceVolume(v) { ambVol = clamp01(v); if (mixer) mixer.setAmbienceVolume(ambVol); setCueLevel(); },
    /** one city sound (a player.sounds id) at 0..2x its city's level, in every city that has it; callable before unlock */
    setSoundTrim(id, v) { trims[id] = +v; if (amb) amb.setTrim(id, +v); },
    /** scene = default export of site/js/scenes/<id>.js; time() = unbounded scene seconds (loop time = time() % 240) */
    setScene(sc, t) {
      if (!sc) return;
      scene = sc;
      if (typeof t === 'function') time = t;
      if (amb) amb.setScene(heard(), time, 3);
      if (engine) { engine.setScene(heard()); emit('queue', player.queue); }      // queued titles follow the city
      if (ac && running(ac)) tick();
    },
    /** a site/js/engine/weather.js WEATHERS pick ('city' = the city as designed); callable before unlock. The city sounds
     *  crossfade to the weather's (weatherScene) and the queued titles follow it, like a city switch. */
    setWeather(w) {
      if (w === weather) return;
      weather = w;
      if (scene) player.setScene(scene);
    },
    /** { energy: 'chill'|'balanced'|'upbeat', band: 'full'|'no-drums'|'keys', style: a STYLE_IDS id }, any part optional;
     *  callable before unlock. Energy and style start with the next track (the queue is re-planned); the band changes on
     *  the current track's next bar. */
    setVibe(v) {
      vibe = normVibe(v, vibe);
      if (engine) { engine.setVibe(vibe); emit('queue', player.queue); }
    },
    /** Lofi Cities Radio (§13), or back to your own mix; callable before unlock, and it does nothing in playlist mode.
     *  On: tunes in at once (the song and bar on air now, the band yours) and the station's tracks follow; next() does
     *  nothing, and style and energy wait for your mix. Off: your own mix at once (the station's track fades out like a
     *  skip, the first of your queue, chained from it, starts), and your queue follows it. */
    setRadio(on) {
      if (!!on === radio) return;
      radio = !!on;
      if (radio) clearLayers();                          // the same song for everyone: nobody's own parts over it
      if (engine) {
        engine.follow(radio ? followRadio : null);
        keepRadio();
        if (!radio && engine.plan && engine.plan.radio) {   // off: not a station track a moment longer
          try { engine.next(); } catch (e) { report(e); }
          tick();
        }
      }
      emit('queue', player.queue);
    },
    /** { on, style, name, next: { style, name, at } }: whether it's on, the show on air and the next one (radio.js radioShow) */
    get radio() { return { on: radio, ...radioShow(wallClock()) }; },
    /** The votes' winners from the live worker, [[h, i, k]] (k: the voteOptions index), added to the ones known; the
     *  station plans with them (the picked slot's style, and the rest of its hour again), unknown ones are ignored.
     *  Callable before unlock; a winner too late for the track already built is tuned in (keepRadio). */
    setPicks(list) {
      const now = Math.floor(wallClock() / 3600000);
      let changed = false;
      for (const [h, i, k] of Array.isArray(list) ? list : []) {
        const style = Number.isSafeInteger(h) && Number.isSafeInteger(i) ? voteOptions(h, i)[k] : undefined;
        if (!style || h < now - 1 || picksOf(h).get(i) === style) continue;
        if (!picks.has(h)) picks.set(h, new Map());
        picks.get(h).set(i, style);
        changed = true;
      }
      for (const h of picks.keys()) if (h < now - 1) picks.delete(h);
      if (changed) { emit('queue', player.queue); if (ac && running(ac)) tick(); }
    },
    /** On the radio, the vote while the track playing (or about to, before the first) is on: radio.js voteAfter's
     *  { h, i, at, close, options: [{ style, name }] } and `pick` (the winner's option index once known, else null), or
     *  null: off the radio, or the next track is one of its hour's closing two. */
    get vote() {
      if (!radio) return null;
      const p = engine && engine.plan, s = (p && p.radio && slotOf(p.radio, picksOf)) || tuneSlot(wallClock(), picksOf);
      const v = voteAfter(s, picksOf);
      if (!v) return null;
      const won = picksOf(v.h).get(v.i), pick = v.options.findIndex(o => o.style === won);
      return { ...v, pick: pick >= 0 ? pick : null };
    },
    on(ev, cb) {
      if (!listeners[ev] || typeof cb !== 'function') return () => {};
      listeners[ev].add(cb);
      return () => listeners[ev].delete(cb);
    },
    get state() { return state; },
    get track() { return track; },
    /** the current city's sounds for the mixer: [{ id, kind: 'bed' | 'event', name }], beds first */
    get sounds() { return soundsOf(heard()); },
    get vibe() { return { ...vibe }; },
    /** upcoming tracks, next first: [{ id, ...track fields }] (id = seed in generative mode) */
    get queue() {
      if (playlist) return playlist.queue;
      if (radio && !(entries && entries.length)) {     // the station's next tracks (before the first one too)
        const p = engine && engine.plan, s = p && p.radio ? slotOf(p.radio, picksOf) : null;
        return upcoming(s || tuneSlot(wallClock(), picksOf), 5, picksOf).map(x => ({ id: x.plan.seed, ...trackInfo(radioPlan(x)) }));
      }
      return engine ? engine.queue.map(p => ({ id: p.seed, ...trackInfo(p) })) : [];
    },
    debug() {
      return {
        liveVoices: registry.live, created: registry.created, reanchors: engine ? engine.reanchors : 0, horizon,
        acState: ac ? ac.state : 'none', latency: latencyOf(ac),
        nodes: registry.nodes, mode: playlist ? 'playlist' : engine ? 'generative' : 'none', clock: clock ? clock.mode : 'none', lite,
        cues: registry.count('cue:ev'), cueLevel: cueBus ? +cueBus.gain.value.toFixed(4) : null,
        taps: ac ? tapEnds.filter(end => end > ac.currentTime).length : 0, tapVoices: registry.count('tap:ev'), tapped, lastTap,
        jam: jamKit ? { ...jamKit.stats(), log: jamLog.slice() } : null,
        layers: layerKit ? { ...layerKit.stats(), now: ac.currentTime, start: engine ? engine.startTime : null, bar: engine && engine.plan ? 240 / engine.plan.bpm : null } : null,
        fading: Boolean(fadeTimer), master: mixer ? +mixer.master.gain.value.toFixed(4) : null,
        tick: { n: tickStats.n, avgMs: tickStats.n ? +(tickStats.totalMs / tickStats.n).toFixed(3) : 0, maxMs: +tickStats.maxMs.toFixed(3), lastMs: +tickStats.lastMs.toFixed(3), over05ms: tickStats.slow },
        engine: engine ? engine.stats() : null, ambience: amb ? amb.stats() : null, radio: radioDebug(),
      };
    },
    /** Tear the player down for good, for tools/browser/audio-check.html, which runs one player after another on one page (the app
     *  keeps its single player): a paused player's pending suspend and clock would otherwise act on the page's context once
     *  the next player runs it. Drops the clock, timers and listeners and closes the context, so the next player's unlock()
     *  makes a fresh one. Every method is a no-op afterwards; resolves when the context is closed. */
    dispose() {
      if (disposed) return Promise.resolve();
      disposed = true; want = false; starting = null;
      clearTimeout(suspendTimer); clearTimeout(fadeTimer); fadeTimer = 0;
      if (unwatch) unwatch();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
      if (typeof removeEventListener === 'function') { removeEventListener('pageshow', onPageShow); removeEventListener('pagehide', onPageHide); }
      if (clock) clock.stop();
      if (playlist) playlist.dispose();                  // its <audio> decks live outside the context
      const closed = closeContext(ac);                   // stops every node of the engine, ambience and mixer
      ac = mixer = engine = amb = playlist = clock = cueBus = unwatch = jamKit = layerKit = null;
      tapEnds = [];
      return closed;
    },
  };
  return player;
}
